/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { IEditor } from "../../IEditor";
import { TOAST_TYPE } from "../../toasts/IToast";
import { TestView, TestViewComponent } from "./TestView";

export function TestModule(dc: IEditor): void {
	dc.get("logger").debug("Test module enabled.");

	const viewSvc = dc.get("viewService");
	const commandsSvc = dc.get("commandsService");
	const keyBindingSvc = dc.get("keyBindingService");
	const toastSvc = dc.get("toastService");

	viewSvc.registerView("test", TestView, TestViewComponent);

	commandsSvc.register({
		id: "test.alert",
		label: "Test: Show alert message",
		args: [
			{
				label: "Message",
				isOptional: true,
				schema: {
					type: "string"
				}
			}
		],
		returns: "None",
		handler: async (args) => alert("Test: " + args[0])
	});

	commandsSvc.register({
		id: "test.toast.info",
		label: "Test: Show info toast",
		args: [
			{
				label: "Message",
				isOptional: false,
				schema: {
					type: "string"
				}
			},
			{
				label: "Details",
				isOptional: true,
				schema: {
					type: "string"
				}
			},
			{
				label: "Icon",
				isOptional: true,
				schema: {
					type: "string"
				}
			}
		],
		returns: "None",
		handler: async (args) => {
			toastSvc.add({
				type: TOAST_TYPE.INFO,
				message: args[0] as string,
				details: args[1] as string,
				icon: args[2] as string,
				isClosable: true,
				timeoutMs: 0
			});
		}
	});

	commandsSvc.register({
		id: "test.toast.success",
		label: "Test: Show success toast",
		args: [
			{
				label: "Message",
				isOptional: false,
				schema: {
					type: "string"
				}
			},
			{
				label: "Details",
				isOptional: true,
				schema: {
					type: "string"
				}
			},
			{
				label: "Icon",
				isOptional: true,
				schema: {
					type: "string"
				}
			}
		],
		returns: "None",
		handler: async (args) => {
			toastSvc.add({
				type: TOAST_TYPE.SUCCESS,
				message: args[0] as string,
				details: args[1] as string,
				icon: args[2] as string,
				isClosable: true,
				timeoutMs: 2000,
				actions: [
					{
						label: "Undo",
						onClick: () => null
					}
				]
			});
		}
	});

	commandsSvc.register({
		id: "test.toast.warning",
		label: "Test: Show warning toast",
		args: [
			{
				label: "Message",
				isOptional: false,
				schema: {
					type: "string"
				}
			},
			{
				label: "Details",
				isOptional: true,
				schema: {
					type: "string"
				}
			},
			{
				label: "Icon",
				isOptional: true,
				schema: {
					type: "string"
				}
			}
		],
		returns: "None",
		handler: async (args) => {
			toastSvc.add({
				type: TOAST_TYPE.WARNING,
				message: args[0] as string,
				details: args[1] as string,
				icon: args[2] as string,
				isClosable: true,
				timeoutMs: 0
			});
		}
	});

	commandsSvc.register({
		id: "test.toast.error",
		label: "Test: Show error toast",
		args: [
			{
				label: "Message",
				isOptional: false,
				schema: {
					type: "string"
				}
			},
			{
				label: "Details",
				isOptional: true,
				schema: {
					type: "string"
				}
			},
			{
				label: "Icon",
				isOptional: true,
				schema: {
					type: "string"
				}
			}
		],
		returns: "None",
		handler: async (args) => {
			toastSvc.add({
				type: TOAST_TYPE.ERROR,
				message: args[0] as string,
				details: args[1] as string,
				icon: args[2] as string,
				isClosable: true,
				timeoutMs: 0,
				actions: [
					{
						label: "Show error log",
						onClick: () => null
					}
				]
			});
		}
	});

	commandsSvc.register({
		id: "test.toast.loading",
		label: "Test: Show loading toast",
		args: [
			{
				label: "Message",
				isOptional: false,
				schema: {
					type: "string"
				}
			},
			{
				label: "Details",
				isOptional: true,
				schema: {
					type: "string"
				}
			},
			{
				label: "Icon",
				isOptional: true,
				schema: {
					type: "string"
				}
			}
		],
		returns: "None",
		handler: async (args) => {
			const id = toastSvc.add({
				type: TOAST_TYPE.LOADING,
				message: args[0] as string,
				details: args[1] as string,
				icon: args[2] as string,
				isClosable: false,
				timeoutMs: 0
			});

			setTimeout(() => {
				toastSvc.update(id, {
					type: TOAST_TYPE.SUCCESS,
					isClosable: true,
					actions: [
						{
							label: "Custom Action",
							onClick: () => null
						},
						{
							label: "Another Action",
							onClick: () => null
						}
					],
					timeoutMs: 3000
				});
			}, 2000);
		}
	});

	commandsSvc.register({
		id: "test.toast.loading2",
		label: "Test: Show loading toast #2",
		args: [],
		returns: "None",
		handler: async () => {
			const id = toastSvc.add({
				type: TOAST_TYPE.LOADING,
				message: "Saving...",
				isClosable: false,
				timeoutMs: 0
			});

			setTimeout(() => {
				toastSvc.remove(id);

				toastSvc.add({
					type: TOAST_TYPE.SUCCESS,
					message: "View has been saved.",
					isClosable: true,
					timeoutMs: 2000
				});
			}, 2000);
		}
	});

	commandsSvc.register({
		id: "test.toast.loading3",
		label: "Test: Show loading toast #3",
		args: [],
		returns: "None",
		handler: async () => {
			const id = toastSvc.add({
				type: TOAST_TYPE.LOADING,
				message: "Saving...",
				isClosable: false,
				timeoutMs: 0
			});

			setTimeout(() => {
				toastSvc.update(id, {
					type: TOAST_TYPE.SUCCESS,
					message: "View has been saved.",
					isClosable: true,
					timeoutMs: 0
				});
			}, 2000);
		}
	});

	// keyBindingSvc.register({
	// 	id: "test.showAlert",
	// 	label: "Test: Show test alert",
	// 	commandId: "test.alert",
	// 	commandArgs: [ "Via key binding" ],
	// 	keys: [{
	// 		key: {
	// 			ctrlKey: true,
	// 			altKey: false,
	// 			shiftKey: true,
	// 			metaKey: false,
	// 			keyCode: KEY_CODE.KEY_A
	// 		},
	// 		isWin: true,
	// 		isLinux: true,
	// 		isMac: true
	// 	}]
	// });

	// keyBindingSvc.register({
	// 	id: "test.showAlertWin",
	// 	label: "Test: Show test alert",
	// 	commandId: "test.alert",
	// 	commandArgs: [ "Via key binding for Windows Only" ],
	// 	keys: [{
	// 		key: {
	// 			ctrlKey: true,
	// 			altKey: false,
	// 			shiftKey: false,
	// 			metaKey: false,
	// 			keyCode: KEY_CODE.KEY_S
	// 		},
	// 		isWin: true,
	// 		isLinux: false,
	// 		isMac: false
	// 	}]
	// });

	// keyBindingSvc.register({
	// 	id: "test.showAlertLinux",
	// 	label: "Test: Show test alert",
	// 	commandId: "test.alert",
	// 	commandArgs: [ "Via key binding for Linux Only" ],
	// 	keys: [{
	// 		key: {
	// 			ctrlKey: true,
	// 			altKey: false,
	// 			shiftKey: false,
	// 			metaKey: false,
	// 			keyCode: KEY_CODE.KEY_D
	// 		},
	// 		isWin: false,
	// 		isLinux: true,
	// 		isMac: false
	// 	}]
	// });

	// keyBindingSvc.register({
	// 	id: "test.showAlertMac",
	// 	label: "Test: Show test alert",
	// 	commandId: "test.alert",
	// 	commandArgs: [ "Via key binding for Mac Only" ],
	// 	keys: [{
	// 		key: {
	// 			ctrlKey: true,
	// 			altKey: false,
	// 			shiftKey: false,
	// 			metaKey: false,
	// 			keyCode: KEY_CODE.KEY_F
	// 		},
	// 		isWin: false,
	// 		isLinux: false,
	// 		isMac: true
	// 	}]
	// });
}
